import React, { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import { Col, Container, Row, Card, Dropdown } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";

import { Breadcrumb } from "@components/Breadcrumb";
import Spinner from "@components/Spinner/Spinner";
import ServerError from "@components/Error/ServerError";
import {
  CalendarWeekIcon,
  ClipboardDataIcon,
  LineChartIcon,
  EditIcon,
  TrashIcon,
  MoreIcon,
} from "@components/icons";
import { ViewTypeButton } from "../Button";
import { deleteProject, getProject } from "@modules/Projects/services/projectCrud";
import { createScheduledQueryFn, REQUEST_PRIORITIES } from "@helper/requestPriority";
import ProjectForm from "@modules/Projects/components/Form";
import { getAllTasks } from "@modules/Tasks/services/taskCrud";
import useChoices from "@hooks/useChoices";
import "@modules/Dashboard/Dashboard.scss";
import { Weather } from "@modules/Weather";
import ReactEcharts from "echarts-for-react";
import { BLUE, GREEN, GREY, RED, YELLOW, shadeColor } from "@helper/colors";

const DetailRow = ({ label, value }) => (
  <div className="project-dashboard-detail-row">
    <div className="project-dashboard-detail-label">{label}</div>
    <div className="project-dashboard-detail-value">{value || "—"}</div>
  </div>
);

const pieStyle = {
  height: "320px",
  width: "100%",
};

const TaskTypePieChart = ({ data }) => {
  const { t } = useTranslation();
  const palette = [BLUE, GREEN, YELLOW, RED, shadeColor(BLUE, -20), shadeColor(GREY, -10)];
  const chartData = data.map(([label, value], index) => ({
    name: label,
    value,
    itemStyle: {
      color: palette[index % palette.length],
    },
  }));
  const total = chartData.reduce((sum, entry) => sum + (Number(entry.value) || 0), 0);
  const option = {
    tooltip: {
      trigger: "item",
    },
    legend: {
      show: false,
    },
    series: [
      {
        name: t("tasks"),
        type: "pie",
        radius: ["62%", "90%"],
        center: ["50%", "48%"],
        avoidLabelOverlap: false,
        label: {
          show: false,
        },
        labelLine: {
          show: false,
        },
        data: chartData,
      },
    ],
  };

  return (
    <Card className="dashboard-panel mb-3">
      <Card.Header className="dashboard-panel-header">
        <h6 className="mb-0">{t("tasks")}</h6>
      </Card.Header>
      <Card.Body>
        {chartData.length > 0 ? (
          <>
            <ReactEcharts option={option} style={pieStyle} notMerge />
            <div className="dashboard-health-overview">
              <div className="dashboard-health-title">{t("dashboard.health_overview")}</div>
              <Row>
                {chartData.map((entry) => {
                  const percentage = total > 0 ? Math.round((entry.value / total) * 100) : 0;
                  return (
                    <Col lg="6" md={6} xs={6} key={entry.name} className="dashboard-health-item">
                      <span className="dashboard-health-label">{entry.name}</span>
                      <div className="dashboard-health-stats">
                        <span
                          className="dashboard-health-dot"
                          style={{ background: entry.itemStyle.color }}
                        />
                        <span className="dashboard-health-value">{entry.value}</span>
                        <span className="dashboard-health-percent">{percentage}%</span>
                      </div>
                    </Col>
                  );
                })}
              </Row>
            </div>
          </>
        ) : (
          <div style={{ fontSize: "13px", color: "#9ca3af" }}>{t("dashboard.no_data")}</div>
        )}
      </Card.Body>
    </Card>
  );
};

const HealthStatePieChart = ({ treeStatus }) => {
  const { t } = useTranslation();
  const data = [
    {
      name: t("health_states.healthy"),
      value: treeStatus.healthy || 0,
      itemStyle: { color: GREEN },
    },
    {
      name: t("health_states.drying_out"),
      value: treeStatus.drying_out || 0,
      itemStyle: { color: YELLOW },
    },
    {
      name: t("health_states.dry"),
      value: treeStatus.dry || 0,
      itemStyle: { color: RED },
    },
    {
      name: t("health_states.frost"),
      value: treeStatus.frost || 0,
      itemStyle: { color: BLUE },
    },
    {
      name: t("health_states.too_wet"),
      value: treeStatus.too_wet || 0,
      itemStyle: { color: shadeColor(BLUE, 10) },
    },
    {
      name: t("health_states.unknown"),
      value: treeStatus.unknown || 0,
      itemStyle: { color: GREY },
    },
  ].filter((entry) => entry.value > 0);

  const total = data.reduce((sum, entry) => sum + (Number(entry.value) || 0), 0);
  const option = {
    tooltip: {
      trigger: "item",
    },
    legend: {
      show: false,
    },
    series: [
      {
        name: t("dashboard.tree_health"),
        type: "pie",
        radius: ["62%", "90%"],
        center: ["50%", "48%"],
        avoidLabelOverlap: false,
        label: {
          show: false,
        },
        labelLine: {
          show: false,
        },
        data,
      },
    ],
  };

  return (
    <Card className="dashboard-panel">
      <Card.Header className="dashboard-panel-header">
        <h6 className="mb-0">{t("dashboard.tree_health")}</h6>
      </Card.Header>
      <Card.Body>
        {data.length > 0 ? (
          <>
            <ReactEcharts option={option} style={pieStyle} notMerge />
            <div className="dashboard-health-overview">
              <div className="dashboard-health-title">{t("dashboard.health_overview")}</div>
              <Row>
                {data.map((entry) => {
                  const percentage = total > 0 ? Math.round((entry.value / total) * 100) : 0;
                  return (
                    <Col lg="6" md={6} xs={6} key={entry.name} className="dashboard-health-item">
                      <span className="dashboard-health-label">{entry.name}</span>
                      <div className="dashboard-health-stats">
                        <span
                          className="dashboard-health-dot"
                          style={{ background: entry.itemStyle.color }}
                        />
                        <span className="dashboard-health-value">{entry.value}</span>
                        <span className="dashboard-health-percent">{percentage}%</span>
                      </div>
                    </Col>
                  );
                })}
              </Row>
            </div>
          </>
        ) : (
          <div style={{ fontSize: "13px", color: "#9ca3af" }}>{t("dashboard.no_health_data")}</div>
        )}
      </Card.Body>
    </Card>
  );
};

export default function ProjectDetailDashboardView({ project, setProject }) {
  const { pid } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [openForm, setOpenForm] = useState(false);
  const [isUpdate, setIsUpdate] = useState(false);
  const [phases] = useChoices("growth_phase");

  const shouldFetchProject = pid !== "without-project" && (!project || project?.id !== +pid);
  const scheduledGetProject = createScheduledQueryFn(
    () => getProject(pid),
    REQUEST_PRIORITIES.HIGH,
    "getProject"
  );

  const {
    isLoading,
    data: projectFetched,
    isError,
  } = useQuery(["project", pid], scheduledGetProject, {
    enabled: shouldFetchProject,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    onSuccess: (data) => {
      setProject?.(data);
    },
  });

  const effectiveProject = shouldFetchProject ? projectFetched : project;

  const { mutateAsync: projectDelete } = useMutation(deleteProject, {
    onSuccess: () => {
      queryClient.invalidateQueries("projects");
      queryClient.invalidateQueries(["project", pid]);
    },
  });

  const detailItems = useMemo(() => {
    if (!effectiveProject) return [];
    return [
      {
        label: t("project.aim"),
        value: effectiveProject.aim ? t(`project.aim_${effectiveProject.aim}`) : null,
      },
      { label: t("project.description"), value: effectiveProject.description },
      { label: t("project.research_question"), value: effectiveProject.research_question },
      { label: t("project.methodology"), value: effectiveProject.methodology },
      { label: t("project.results"), value: effectiveProject.findings },
      { label: t("project.challenges"), value: effectiveProject.challenges },
      { label: t("project.materials"), value: effectiveProject.materials },
    ].filter((item) => item.value);
  }, [effectiveProject, t]);

  const treeIds = useMemo(() => {
    if (!effectiveProject?.areas) return [];
    return effectiveProject.areas
      .flatMap((area) => area.tree_set || [])
      .map((tree) => tree?.id)
      .filter(Boolean);
  }, [effectiveProject]);

  const { data: taskData } = useQuery(
    ["projectTasks", pid],
    async () => {
      try {
        return await getAllTasks({ projectId: pid });
      } catch (error) {
        const fallback = await getAllTasks();
        return { fallback, __fallbackAll: true };
      }
    },
    {
      enabled: Boolean(pid),
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 5,
    }
  );

  const projectTasks = useMemo(() => {
    if (!taskData) return null;
    const base = taskData.__fallbackAll ? taskData.fallback : taskData;
    const items = Array.isArray(base?.results)
      ? base.results
      : Array.isArray(base)
      ? base
      : Array.isArray(base?.data)
      ? base.data
      : [];
    if (!treeIds.length) return items;
    return items.filter((task) => {
      const treeId = task.treeId || task.tree_id || task.tree?.id;
      return treeIds.includes(treeId);
    });
  }, [taskData, treeIds]);

  const taskCount = projectTasks ? projectTasks.length : null;

  const { pastTasks, futureTasks } = useMemo(() => {
    if (!projectTasks) return { pastTasks: null, futureTasks: null };
    const now = new Date();
    let past = 0;
    let future = 0;
    projectTasks.forEach((task) => {
      const scheduledAt =
        task.scheduledAt || task.scheduled_at || task.dueDate || task.due_date || task.dueDateTime;
      if (!scheduledAt) return;
      const date = new Date(scheduledAt);
      if (Number.isNaN(date.getTime())) return;
      if (date < now) past += 1;
      else future += 1;
    });
    return { pastTasks: past, futureTasks: future };
  }, [projectTasks]);

  const tasksByType = useMemo(() => {
    if (!projectTasks) return [];
    const counts = new Map();
    projectTasks.forEach((task) => {
      const type = task.measureType || task.measure_type || task.title || task.type || t("other");
      counts.set(type, (counts.get(type) || 0) + 1);
    });
    return Array.from(counts.entries()).sort((a, b) => b[1] - a[1]);
  }, [projectTasks, t]);

  const projectTrees = useMemo(() => {
    if (!effectiveProject?.areas) return [];
    return effectiveProject.areas.flatMap((area) => area.tree_set || []);
  }, [effectiveProject]);

  const phaseLookup = useMemo(() => {
    if (!phases) return new Map();
    return new Map(phases.map((phase) => [Number(phase.key), t(phase.value)]));
  }, [phases, t]);

  const treePhaseStats = useMemo(() => {
    const counts = new Map();
    projectTrees.forEach((tree) => {
      const phase = tree?.phase ?? tree?.tree_phase;
      const label = phase == null ? t("health_states.unknown") : phaseLookup.get(Number(phase));
      const normalizedLabel = label || t("health_states.unknown");
      counts.set(normalizedLabel, (counts.get(normalizedLabel) || 0) + 1);
    });
    return Array.from(counts.entries()).sort((a, b) => b[1] - a[1]);
  }, [projectTrees, phaseLookup, t]);

  const healthStateMap = useMemo(() => {
    return projectTrees.reduce(
      (acc, tree) => {
        const state = tree?.health_state;
        if (state === 0) acc.healthy += 1;
        else if (state === 1) acc.drying_out += 1;
        else if (state === 2) acc.dry += 1;
        else if (state === 4) acc.frost += 1;
        else if (state === 5) acc.too_wet += 1;
        else acc.unknown += 1;
        return acc;
      },
      { healthy: 0, drying_out: 0, dry: 0, frost: 0, too_wet: 0, unknown: 0 }
    );
  }, [projectTrees]);

  if (isLoading) return <Spinner height={"80vh"} />;
  if (isError) return <ServerError />;

  const handleEditProject = () => {
    setIsUpdate(true);
    setOpenForm(true);
  };

  const handleDeleteProject = async () => {
    if (!effectiveProject?.id) return;
    const result = await Swal.fire({
      title: t("confirm_delete"),
      text: `${t("project.name")}: ${effectiveProject?.name || ""}`,
      icon: "warning",
      showCancelButton: true,
      reverseButtons: true,
      confirmButtonColor: "#EF3600",
      cancelButtonColor: "#286CA5",
      cancelButtonText: t("cancel"),
      confirmButtonText: t("delete"),
    });
    if (!result.isConfirmed) return;
    await projectDelete(effectiveProject.id);
    navigate("/projects");
  };

  return (
    <>
      <Breadcrumb
        title={
          <span className="d-inline-flex align-items-center gap-3 flex-wrap">
            <span>{effectiveProject?.name || t("projects")}</span>
            <Weather weather={effectiveProject?.weather} iconWidth={"1.2rem"} hideLabel />
          </span>
        }
        viewType={<ViewTypeButton id={pid} />}
      />
      <Container>
        <Row className="g-3">
          <Col lg={7}>
            <Card className="dashboard-panel mb-3">
              <Card.Header className="dashboard-panel-header">
                <h6 className="mb-0">{t("dashboard.title")}</h6>
              </Card.Header>
              <Card.Body>
                <div className="dashboard-kpi-group-grid">
                  <div className="dashboard-kpi-item">
                    <div className="dashboard-kpi-item-top">
                      <div className="dashboard-kpi-item-icon">
                        <ClipboardDataIcon />
                      </div>
                      <div className="dashboard-kpi-item-value">
                        <span className="dashboard-kpi-item-number">
                          {taskCount == null ? "—" : taskCount}
                        </span>
                      </div>
                    </div>
                    <div className="dashboard-kpi-item-text">
                      <span>{t("tasks")}</span>
                    </div>
                  </div>
                  <div className="dashboard-kpi-item">
                    <div className="dashboard-kpi-item-top">
                      <div className="dashboard-kpi-item-icon">
                        <CalendarWeekIcon />
                      </div>
                      <div className="dashboard-kpi-item-value">
                        <span className="dashboard-kpi-item-number">
                          {pastTasks == null ? "—" : pastTasks}
                        </span>
                      </div>
                    </div>
                    <div className="dashboard-kpi-item-text">
                      <span>{t("past_tasks")}</span>
                    </div>
                  </div>
                  <div className="dashboard-kpi-item">
                    <div className="dashboard-kpi-item-top">
                      <div className="dashboard-kpi-item-icon">
                        <LineChartIcon />
                      </div>
                      <div className="dashboard-kpi-item-value">
                        <span className="dashboard-kpi-item-number">
                          {futureTasks == null ? "—" : futureTasks}
                        </span>
                      </div>
                    </div>
                    <div className="dashboard-kpi-item-text">
                      <span>{t("future_tasks")}</span>
                    </div>
                  </div>
                </div>
              </Card.Body>
            </Card>
            <TaskTypePieChart data={tasksByType} />
            <Card className="dashboard-panel">
              <Card.Header className="dashboard-panel-header">
                <h6 className="mb-0">{t("trees")}</h6>
              </Card.Header>
              <Card.Body>
                <div className="dashboard-health-overview">
                  <div className="dashboard-health-title">{t("tree_phases")}</div>
                  {treePhaseStats.length > 0 ? (
                    treePhaseStats.map(([label, value]) => (
                      <div key={label} className="dashboard-health-item">
                        <div className="dashboard-health-label">{label}</div>
                        <div className="dashboard-health-stats">
                          <span className="dashboard-health-value">{value}</span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div style={{ fontSize: "13px", color: "#9ca3af" }}>—</div>
                  )}
                </div>
              </Card.Body>
            </Card>
            <HealthStatePieChart treeStatus={healthStateMap} />
          </Col>
          <Col lg={5}>
            {openForm ? (
              <div className="project-dashboard-detail-shell">
                <ProjectForm
                  show={true}
                  isUpdate={isUpdate}
                  setOpenForm={setOpenForm}
                  setIsUpdate={setIsUpdate}
                  selectedProject={effectiveProject}
                  renderMode="inline"
                />
              </div>
            ) : (
              <div className="project-dashboard-detail-shell project-dashboard-detail-shell--static">
                <div className="d-flex align-items-start justify-content-between mb-3">
                  <div className="d-flex flex-column">
                    <div className="project-dashboard-detail-label">{t("project.name")}</div>
                    <div className="project-dashboard-detail-value">
                      {effectiveProject?.name || "—"}
                    </div>
                  </div>
                  <Dropdown align="end">
                    <Dropdown.Toggle
                      as="button"
                      className="sensor-detail-icon-link sensor-detail-actions-toggle entity-detail-control border-0"
                      id="project-detail-actions-dropdown"
                      title={t("more_actions", "Mehr Aktionen")}
                    >
                      <MoreIcon size="24px" />
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                      <Dropdown.Item onClick={handleEditProject}>
                        <EditIcon size="16px" /> {t("edit", "Bearbeiten")}
                      </Dropdown.Item>
                      <Dropdown.Item onClick={handleDeleteProject} className="text-danger">
                        <TrashIcon size="16px" /> {t("delete", "Löschen")}
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                </div>
                <div className="d-flex flex-column gap-3">
                  {detailItems.map((item) => (
                    <DetailRow key={item.label} label={item.label} value={item.value} />
                  ))}
                </div>
              </div>
            )}
          </Col>
        </Row>
      </Container>
    </>
  );
}
